import { Box, Stack, Typography } from "@mui/material";

type TExternalStoreNoticeProps = {
  icon: string;
  iconAlt: string;
  title: string;
  subtitle: string;
  stepsTitle: string;
  stepsTitleMobile: string;
  steps: string[];
  illustration?: string;
  illustrationAlt?: string;
};

export const ExternalStoreNotice = ({
  icon,
  iconAlt,
  title,
  subtitle,
  stepsTitle,
  stepsTitleMobile,
  steps,
  illustration,
  illustrationAlt,
}: TExternalStoreNoticeProps) => {
  const hasIllustration = Boolean(illustration);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: { xs: "auto", md: "80vh" },
        px: { xs: 2, md: 0 },
      }}
    >
      <Box
        sx={{
          bgcolor: "#EBF0FB",
          borderRadius: "20px",
          p: { xs: 3, md: 4 },
          width: { xs: "100%", md: "1200px" },
        }}
      >
        <Stack
          direction="row"
          spacing={{ xs: 1.5, md: 3 }}
          alignItems="flex-start"
          mb={{ xs: 1.5, md: 4 }}
        >
          <Box
            component="img"
            src={icon}
            alt={iconAlt}
            sx={{
              width: { xs: 48, md: 120 },
              height: { xs: 48, md: 120 },
              flexShrink: 0,
            }}
          />
          <Box
            pt={{ xs: 0, md: 0.5 }}
            sx={{
              display: { xs: "flex", md: "block" },
              flexDirection: "column",
              justifyContent: "center",
              minHeight: { xs: 48, md: "auto" },
            }}
          >
            <Typography
              fontWeight={700}
              fontSize={{ xs: 18, md: 24 }}
              color="#29282B"
              lineHeight={1.3}
            >
              {title}
            </Typography>
            <Typography
              fontSize={{ md: 18 }}
              color="#29282B"
              fontWeight={400}
              mt={1}
              lineHeight={1.5}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Stack>

        <Typography
          fontSize={14}
          color="#29282B"
          fontWeight={400}
          mb={3}
          lineHeight={1.5}
          sx={{ display: { xs: "block", md: "none" } }}
        >
          {subtitle}
        </Typography>

        <Box
          sx={{
            bgcolor: "white",
            borderRadius: "20px",
            p: { xs: 3, md: 5 },
            position: "relative",
            minHeight: hasIllustration ? { md: 440 } : undefined,
          }}
        >
          <Typography
            fontWeight={700}
            fontSize={{ xs: 16, md: 28 }}
            color="#29282B"
            mb={{ xs: 2, md: 3 }}
          >
            <Box
              component="span"
              sx={{ display: { xs: "none", md: "inline" } }}
            >
              {stepsTitle}
            </Box>
            <Box
              component="span"
              sx={{ display: { xs: "inline", md: "none" } }}
            >
              {stepsTitleMobile}
            </Box>
          </Typography>

          <Box sx={{ pr: hasIllustration ? { xs: 0, md: "340px" } : 0 }}>
            {steps.map((step, i) => (
              <Stack
                key={i}
                direction="row"
                spacing={{ xs: 1.5, md: 2.5 }}
                alignItems="center"
                mb={i < steps.length - 1 ? { xs: 1.5, md: 2 } : 0}
              >
                <Box
                  sx={{
                    width: { xs: 32, md: 42 },
                    height: { xs: 32, md: 42 },
                    borderRadius: "50%",
                    bgcolor: "#0F58CC",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    color="white"
                    fontWeight={600}
                    fontSize={{ xs: 14, md: 18 }}
                  >
                    {i + 1}
                  </Typography>
                </Box>
                <Typography
                  fontSize={{ xs: 14, md: 16 }}
                  fontWeight={500}
                  color="#434343"
                >
                  {step}
                </Typography>
              </Stack>
            ))}
          </Box>

          {illustration && (
            <Box
              component="img"
              src={illustration}
              alt={illustrationAlt}
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                height: 320,
                width: 270,
                objectFit: "contain",
                display: { xs: "none", md: "block" },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
